import React, { useState, useEffect } from "react";
import {
    Button,
    FormControl,
    FormLabel,
    Input,
    InputGroup,
    InputRightElement,
    IconButton,
    Stack,
    Icon,
    Text,
    HStack,
    PinInput,
    PinInputField,
    Box,
    VStack,
} from "@chakra-ui/react";
import { IoWarningOutline, IoArrowBackOutline, IoCheckmarkCircleOutline } from "react-icons/io5";
import { TbEye, TbEyeClosed } from "react-icons/tb";
import {
    sendForgotPasswordOtp,
    verifyPasswordOtp,
    resetPassword,
} from "services/UserService";
import { useUser } from "context/UserContext";
import { FaTimes } from "react-icons/fa";

type Step = "email" | "otp" | "reset" | "success";

interface ForgotPasswordProps {
    /** Pre-fills the email field (e.g. email already typed on login page or from user context). */
    initialEmail?: string;
    /** Called after the user clicks "Back" or "Back to sign in". */
    onCancel?: () => void;
    /** Called after the password is successfully reset. */
    onSuccess?: () => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({
    initialEmail = "",
    onCancel,
    onSuccess,
}) => {
    const { isLoggedIn } = useUser();

    const [step, setStep] = useState<Step>("email");

    // Step 1 — email
    const [email, setEmail] = useState(initialEmail);

    // Step 2 — OTP
    const [otp, setOtp] = useState("");

    // Step 3 — new password
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    // Resend cooldown
    const [resendCountdown, setResendCountdown] = useState(0);

    useEffect(() => {
        if (step !== "otp") return;
        setResendCountdown(60);
        const interval = setInterval(() => {
            setResendCountdown((prev) => {
                if (prev <= 1) { clearInterval(interval); return 0; }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [step]);

    // Shared
    const [resetToken, setResetToken] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [localError, setLocalError] = useState("");

    const clearError = () => setLocalError("");

    const handleSendOtp = async () => {
        if (!email) {
            setLocalError("Please enter your email address.");
            return;
        }
        setIsLoading(true);
        clearError();
        const ok = await sendForgotPasswordOtp(email);
        setIsLoading(false);
        if (ok) setStep("otp");
    };

    const handleVerifyOtp = async () => {
        if (otp.length !== 6) {
            setLocalError("Please enter the full 6-digit code.");
            return;
        }
        setIsLoading(true);
        clearError();
        const token = await verifyPasswordOtp(email, otp);
        setIsLoading(false);
        if (token) {
            setResetToken(token);
            setStep("reset");
        }
    };

    const handleResetPassword = async () => {
        if (!newPassword) {
            setLocalError("Please enter a new password.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setLocalError("Passwords do not match.");
            return;
        }
        setIsLoading(true);
        clearError();
        const ok = await resetPassword(resetToken, newPassword);
        setIsLoading(false);
        if (ok) {
            onSuccess?.();
        }
    };

    const renderError = () =>
        localError ? (
            <HStack spacing={2} align="flex-start">
                <Icon as={IoWarningOutline} color="red.500" boxSize={5} mt="1px" flexShrink={0} />
                <Text fontSize="sm" color="red.500">{localError}</Text>
            </HStack>
        ) : null;

    const renderBackButton = (label = "Back") => (
        <Button variant="link" color="purple.600" size="sm" onClick={onCancel}>
            <Icon as={IoArrowBackOutline} mr={1} />
            {label}
        </Button>
    );

    // ── Step 1: Email ────────────────────────────────────────────────
    if (step === "email") {
        return (
            <VStack spacing={1} width="100%">
                {onCancel && isLoggedIn && (
                    <IconButton
                        aria-label="Cancel"
                        variant="outline"
                        color="red.500"
                        size="lg"
                        onClick={onCancel}
                        icon={<Icon as={FaTimes} color="red.500" />}
                        border="none"
                        alignSelf="flex-end"
                    />
                )}
                <Box width="100%">
                    <Stack spacing={5} width="100%">
                        <Stack spacing={1}>
                            <Text fontSize="lg" fontWeight="600" color="purple.700">
                                Reset your password
                            </Text>
                            <Text fontSize="sm" color="fg.muted">
                                Enter your account email and we'll send you a 6-digit code.
                            </Text>
                        </Stack>
                        <FormControl>
                            <FormLabel fontSize="md" color="fg.muted">Email</FormLabel>
                            <Input
                                type="email"
                                size="lg"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
                                autoFocus
                            />
                        </FormControl>
                        {renderError()}
                        <Button
                            bg="purple.700"
                            color="white"
                            colorScheme="purple"
                            size="lg"
                            width="full"
                            isLoading={isLoading}
                            loadingText="Sending..."
                            onClick={handleSendOtp}
                            _hover={{ bg: "purple.600" }}
                        >
                            Send Code
                        </Button>
                        {onCancel && !isLoggedIn && renderBackButton("Back to sign in")}
                    </Stack>
                </Box>
            </VStack>
        );
    }

    // ── Step 2: OTP ──────────────────────────────────────────────────
    if (step === "otp") {
        return (
            <VStack spacing={1} width="100%">
                {onCancel && isLoggedIn && (
                    <IconButton
                        aria-label="Cancel"
                        variant="outline"
                        color="red.500"
                        size="lg"
                        onClick={onCancel}
                        icon={<Icon as={FaTimes} color="red.500" />}
                        border="none"
                        alignSelf="flex-end"
                    />
                )}
                <Box width="100%">
                    <Stack spacing={5} width="100%">
                        <Stack spacing={1}>
                            <Text fontSize="lg" fontWeight="600" color="purple.700">
                                Check your email
                            </Text>
                            <Text fontSize="sm" color="fg.muted">
                                We sent a 6-digit code to <Text as="b">{email}</Text>. It expires in 10 minutes.
                            </Text>
                        </Stack>
                        <HStack justify="center" spacing={3}>
                            <PinInput size="lg" value={otp} onChange={setOtp} autoFocus>
                                <PinInputField
                                    borderColor="purple.300"
                                    _focus={{ borderColor: "purple.600", boxShadow: "0 0 0 1px #553C9A" }}
                                />
                                <PinInputField
                                    borderColor="purple.300"
                                    _focus={{ borderColor: "purple.600", boxShadow: "0 0 0 1px #553C9A" }}
                                />
                                <PinInputField
                                    borderColor="purple.300"
                                    _focus={{ borderColor: "purple.600", boxShadow: "0 0 0 1px #553C9A" }}
                                />
                                <PinInputField
                                    borderColor="purple.300"
                                    _focus={{ borderColor: "purple.600", boxShadow: "0 0 0 1px #553C9A" }}
                                />
                                <PinInputField
                                    borderColor="purple.300"
                                    _focus={{ borderColor: "purple.600", boxShadow: "0 0 0 1px #553C9A" }}
                                />
                                <PinInputField
                                    borderColor="purple.300"
                                    _focus={{ borderColor: "purple.600", boxShadow: "0 0 0 1px #553C9A" }}
                                />
                            </PinInput>
                        </HStack>
                        {renderError()}
                        <Button
                            bg="purple.700"
                            color="white"
                            colorScheme="purple"
                            size="lg"
                            width="full"
                            isLoading={isLoading}
                            loadingText="Verifying..."
                            onClick={handleVerifyOtp}
                            _hover={{ bg: "purple.600" }}
                        >
                            Verify Code
                        </Button>
                        <HStack justify="center" spacing={1}>
                            <Text fontSize="sm" color="fg.muted" m={0}>Didn't receive it?</Text>
                            <Button
                                variant="link"
                                color={resendCountdown > 0 ? "gray.400" : "purple.600"}
                                size="sm"
                                isDisabled={resendCountdown > 0}
                                onClick={() => { setOtp(""); setStep("email"); }}
                            >
                                {resendCountdown > 0 ? `Resend code (${resendCountdown}s)` : "Resend code"}
                            </Button>
                        </HStack>
                        {onCancel && !isLoggedIn && renderBackButton("Back to sign in")}
                    </Stack>
                </Box>
            </VStack>
        );
    }

    // ── Step 3: New password ─────────────────────────────────────────
    if (step === "reset") {
        return (
            <VStack spacing={1} width="100%">
                {onCancel && isLoggedIn && (
                    <IconButton
                        aria-label="Cancel"
                        variant="outline"
                        color="red.500"
                        size="lg"
                        onClick={onCancel}
                        icon={<Icon as={FaTimes} color="red.500" />}
                        border="none"
                        alignSelf="flex-end"
                    />
                )}
                <Stack spacing={5} width="100%">
                    <Stack spacing={1}>
                        <Text fontSize="lg" fontWeight="600" color="purple.700">
                            Set a new password
                        </Text>
                        <Text fontSize="sm" color="fg.muted">
                            Choose a strong password for your account.
                        </Text>
                    </Stack>
                    <FormControl>
                        <FormLabel fontSize="md" color="fg.muted">New Password</FormLabel>
                        <InputGroup>
                            <Input
                                type={showNew ? "text" : "password"}
                                size="lg"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                autoFocus
                            />
                            <InputRightElement height="100%" alignItems="center" justifyContent="center">
                                <IconButton
                                    variant="ghost"
                                    size="sm"
                                    aria-label={showNew ? "Hide password" : "Show password"}
                                    icon={showNew ? <TbEye /> : <TbEyeClosed />}
                                    onClick={() => setShowNew((v) => !v)}
                                />
                            </InputRightElement>
                        </InputGroup>
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="md" color="fg.muted">Confirm Password</FormLabel>
                        <InputGroup>
                            <Input
                                type={showConfirm ? "text" : "password"}
                                size="lg"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleResetPassword()}
                            />
                            <InputRightElement height="100%" alignItems="center" justifyContent="center">
                                <IconButton
                                    variant="ghost"
                                    size="sm"
                                    aria-label={showConfirm ? "Hide password" : "Show password"}
                                    icon={showConfirm ? <TbEye /> : <TbEyeClosed />}
                                    onClick={() => setShowConfirm((v) => !v)}
                                />
                            </InputRightElement>
                        </InputGroup>
                    </FormControl>
                    {renderError()}
                    <Button
                        bg="purple.700"
                        color="white"
                        colorScheme="purple"
                        size="lg"
                        width="full"
                        isLoading={isLoading}
                        loadingText="Saving..."
                        onClick={handleResetPassword}
                        _hover={{ bg: "purple.600" }}
                    >
                        Set Password
                    </Button>
                    {onCancel && !isLoggedIn && renderBackButton("Back to sign in")}
                </Stack>
            </VStack>
        );
    }

    // ── Step 4: Success ──────────────────────────────────────────────
    return (
        <Stack spacing={5} width="100%" align="center">
            <Icon as={IoCheckmarkCircleOutline} color="green.500" boxSize={12} />
            <Stack spacing={1} align="center">
                <Text fontSize="lg" fontWeight="600" color="green.600">
                    Password updated!
                </Text>
                <Text fontSize="sm" color="fg.muted" textAlign="center">
                    Your password has been changed successfully.
                </Text>
            </Stack>
            <Button
                bg="purple.700"
                color="white"
                colorScheme="purple"
                size="lg"
                width="full"
                onClick={onSuccess}
                _hover={{ bg: "purple.600" }}
            >
                {onCancel ? "Back to Sign In" : "Done"}
            </Button>
        </Stack>
    );
};

export default ForgotPassword;
